<template>
  <v-form ref="stepOrdererRef">
    <v-row>
      <v-col cols="12" lg="6" class="pa-6">
        <household-basic-information
          v-model="formOrdererStore.household"
          required
          with-date-of-birth
          with-managing-director-fields
          :disabled="disableHouseholdBasicForm"
          :with-company-option="!formOrderRequestStore.isOrderingProduct"
        />
        <v-divider class="my-4" />
        <h3 class="pt-4 pb-2">
          Rechnungsadresse
        </h3>
        <v-switch
          v-model="formOrdererStore.invoiceAddressIsConnectionAddress"
          label="Rechnungsadresse und Hausanschlussadresse sind identisch"
          hide-details
          class="mb-2"
        />
        <address-form
          v-if="!formOrdererStore.invoiceAddressIsConnectionAddress"
          v-model="formOrdererStore.invoiceAddress"
          required
        />
      </v-col>
      <v-col cols="12" lg="6" class="pa-6">
        <h3 class="pb-4">
          Kontaktdaten
        </h3>
        <household-contact-data
          v-model="formOrdererStore.contact"
          :phone-prefixes="initStore.phonePrefixes!"
        />
        <v-divider class="my-4" />
        <h3 class="py-4">
          Weitere Punkte
        </h3>

        <v-col cols="12" class="px-0">
          <v-textarea
            v-model="formOrdererStore.additionalInfo"
            auto-grow
            class="pr-2"
            counter
            label="Ergänzende Informationen zum Auftrag"
            :rules="rulesAdditionalInfo"
          />
        </v-col>

        <h4 class="mb-2">
          Bezug zum Anschlussobjekt<sup v-if="mandatoryBoolean" class="required">*</sup>
        </h4>
        <v-radio-group
          v-if="bookingStore.currentBookingMode !== BookingMode.productOnlyMph"
          v-model="formOrdererStore.relationToBuilding"
          :rules="buildRulesRequiredDefined<OrdererRelationToBuilding>()"
          class="mt-0"
        >
          <v-radio
            label="Auftraggeber ist Eigentümer des Anschlussobjekts"
            :value="OrdererRelationToBuilding.IS_OWNER"
          />
          <v-radio
            label="Auftraggeber ist Verwalter des Anschlussobjekts"
            :value="OrdererRelationToBuilding.IS_ADMINISTRATOR"
          />
          <v-radio
            v-if="!formOrderRequestStore.isOrderingForMph"
            label="Auftraggeber ist nicht Eigentümer/Verwalter des Anschlussobjekts"
            :value="OrdererRelationToBuilding.IS_NOT_OWNER_OR_ADMINISTRATOR"
          />
        </v-radio-group>
        <v-checkbox
          v-model="formOrdererStore.contactingAppointment"
          label="Kunde ist mit Kontaktierung zwecks Terminabsprache einverstanden"
          hide-details
        />
      </v-col>
    </v-row>
    <v-dialog v-model="showDialogForOtherOwnerOrManager" max-width="600">
      <v-card>
        <v-toolbar density="compact" title="Abweichender Eigentümer/Verwalter" />
        <v-card-text>
          Eine Beauftragung ist möglich, der Auftraggeber muss aber selbstständig den
          Eigentümer/Verwalter zum Ausbau der Infrastruktur informieren.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="elevated" @click="showDialogForOtherOwnerOrManager = false">
            Verstanden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>
<script setup lang="ts">
import { ref, watch } from 'vue';
import { mandatoryBoolean, buildRulesRequiredDefined } from '../composables/validations';
import AddressForm from './common/address-form.vue';
import HouseholdBasicInformation from './common/household-basic-information.vue';
import HouseholdContactData from './common/household-contact-data.vue';
import { checkIfHouseholdExists } from '../services/api';
import useBookingStore from '../store/booking';
import useFormOrderRequestStore from '../store/form-order-request';
import useFormOrdererStore from '../store/form-orderer';
import useInitStore from '../store/init';
import useOrderStore from '../store/order';
import { OrdererRelationToBuilding } from '../types/step-data';
import { BookingMode } from '../types/order-main';

const rulesAdditionalInfo = [
  (v?: string) => !v || v.length <= 600 || 'Maximal 600 Zeichen',
];

const bookingStore = useBookingStore();
const formOrderRequestStore = useFormOrderRequestStore();
const formOrdererStore = useFormOrdererStore();
const initStore = useInitStore();

const stepOrdererRef = ref();
const showDialogForOtherOwnerOrManager = ref(false);
const disableHouseholdBasicForm = ref(!!formOrdererStore.household);

const isValid = async () => {
  const { valid } = await stepOrdererRef.value.validate();
  if (!valid) {
    return false;
  }

  if (formOrdererStore.household?.id) {
    return true;
  }

  const {
    firstName,
    lastName,
    isCompany,
    dateOfBirth,
    registerNumber,
  } = formOrdererStore.household ?? {};

  const [error, data] = await checkIfHouseholdExists(
    initStore.addressId!,
    firstName!,
    lastName!,
    isCompany ?? false,
    dateOfBirth ?? undefined,
    registerNumber ?? undefined,
  );
  if (error || !data) {
    return false;
  }

  if (data.isExisting && data.result?.household?.id) {
    formOrdererStore.addHouseholdId(data.result.household.id);
  }
  return true;
};

watch(() => formOrdererStore.relationToBuilding, value => {
  if (
    value === OrdererRelationToBuilding.IS_NOT_OWNER_OR_ADMINISTRATOR
    && bookingStore.currentBookingMode !== BookingMode.productOnlyMph
  ) {
    showDialogForOtherOwnerOrManager.value = true;
  }
});

/**
 * This mode tells us that the connection address is an MPH and there is no infrastructure.
 * Customers that order products this way are not owners or administrators of the building.
 */
if (bookingStore.currentBookingMode === BookingMode.productOnlyMph) {
  formOrdererStore.relationToBuilding = OrdererRelationToBuilding.IS_NOT_OWNER_OR_ADMINISTRATOR;
}

defineExpose({
  applyData: () => {
    useOrderStore().addOrdererData(formOrdererStore.stepData);
  },
  isValid,
});
</script>
